import React, { useEffect, useState, useCallback } from 'react';
import {
    DialogContent,
    Button,
    Stack,
    Typography,
    Alert,
    Box,
    IconButton,
    TextField,
    Divider,
} from '@mui/material';
import { Close as CloseIcon, Add } from '@mui/icons-material';
import { Shipment } from '@domain/shipment';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import {
    ProductDisplay,
    WarehouseSelection,
    LeadTimeTimeline
} from '@features/shipments/components/shared';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import CreateShipmentMethodDialog from '@features/shipments/components/methods/CreateShipmentMethodDialog';
import { ShipmentMethodPreview } from '@shared/ui/common/ShipmentMethodPreview';
import { useShipment, useShipments } from '@features/shipments/hooks/useShipments';

interface EditShipmentDetailsProps {
    onFinish: (shipment?: Shipment) => void;
    onClose?: () => void;
    shipmentId: string;
}

export const EditShipmentDetails: React.FC<EditShipmentDetailsProps> = ({
    onFinish,
    onClose,
    shipmentId
}) => {
    const { warehouses } = useWarehouses();
    const { updateShipment } = useShipments();

    const [error, setError] = useState<string | null>(null);

    const { shipment } = useShipment(shipmentId);

    const [updatedShipment, setUpdatedShipment] = useState<Shipment | undefined>(shipment);

    useEffect(() => {
        if (shipment) {
            setUpdatedShipment(shipment);
        }
    }, [shipment]);

    const updateShipmentValues = (shipment: Partial<Shipment>) => {
        setUpdatedShipment(prev => ({ ...prev, ...shipment } as Shipment));
    }


    const handleSubmit = async () => {
        if (!updatedShipment) return;

        try {
            setError(null);
            const updatedShipmentResult = await updateShipment({
                id: updatedShipment.id!,
                request: updatedShipment,
            });
            onFinish(updatedShipmentResult);
        } catch (error) {
            console.error('Error updating shipment:', error);
            setError('Failed to update shipment. Please try again.');
        }
    };


    return (
        <>
            <DialogHeader>
                <Stack direction="row" justifyContent="flex-start" alignItems="center" width="100%">

                    <Typography variant="h6" flexGrow={1}>Edit Shipment</Typography>

                    {onClose && (
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogHeader>

            <DialogContent sx={{ p: 4, gap: 4, display: 'flex', flexDirection: 'column' }}>


                <Stack sx={{ p: 0 }} gap={2}>
                    <Typography variant="subtitle2" color="text.primary">Products in shipment</Typography>
                    <ProductDisplay purchaseOrderItems={updatedShipment?.purchaseOrderItems || []} />
                </Stack>

                <Divider />


                <Stack flex={1} gap={4}>

                    {updatedShipment?.transportMethod && (
                        <Stack gap={4}>
                            <WarehouseSelection
                                warehouses={warehouses || []}
                                selectedWarehouseId={updatedShipment?.destinationWarehouse?.id || ''}
                                onWarehouseChange={(warehouseId) => updateShipmentValues({ destinationWarehouse: warehouses?.find(w => w.id === warehouseId) })}
                            />



                            <LeadTimeTimeline
                                departureDate={updatedShipment?.departureDate}
                                arrivalDate={updatedShipment?.arrivalDate}
                                processedDate={updatedShipment?.processedDate}
                                onDepartureDateChange={(date) => updateShipmentValues({ departureDate: date })}
                                onArrivalDateChange={(date) => updateShipmentValues({ arrivalDate: date })}
                                onProcessedDateChange={(date) => updateShipmentValues({ processedDate: date })}
                            />

                            <Stack spacing={1} justifyContent="flex-start" alignItems="flex-start">
                                <Typography variant="subtitle2" color="text.primary">
                                    Shipment Name
                                </Typography>
                                <TextField
                                    value={updatedShipment?.name}
                                    onChange={(e) => updateShipmentValues({ name: e.target.value })}
                                    variant="outlined"
                                    size="small"
                                />
                            </Stack>

                        </Stack>
                    )}

                </Stack>

                {error && (
                    <Alert severity="error">
                        {error}
                    </Alert>
                )}

            </DialogContent >

            {/* Bottom Action Bar */}
            < Box sx={{
                py: 3,
                px: 4,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper'
            }
            }>
                <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        Update Shipment
                    </Button>
                </Stack>
            </Box >

        </>

    );
};

export default EditShipmentDetails;
